// KNP (Key Names and Phrases) Store - Svelte 5
import { authState } from "./authStore.svelte.js";
import firebase from "@app/configs/firebase.js";
import db from "@app/configs/firestore.js";

// Create reactive state for KNP management
export const knpState = $state({
    guides: [],
    selectedGuide: null,
    terms: [],
    loading: false,
    error: null
});

// Helper function to check if current user can access a guide based on role and group membership
export function canUserAccessGuide(guide) {
    // If no auth state or user, deny access
    if (!authState.user) {
        return false;
    }
    
    // Admin and Manager roles always have access to all guides
    if (authState.user.role === "admin" || authState.user.role === "manager") {
        return true;
    }
    
    // If guide has no groups assigned (or undefined), it's accessible to all team members
    if (!guide.groups || guide.groups.length === 0) {
        return true;
    }
    
    // Check if user is a member of any of the guide's assigned groups
    if (authState.user.groups && authState.user.groups.length > 0) {
        return guide.groups.some(groupId => authState.user.groups.includes(groupId));
    }
    
    // If user has no groups, they can't access group-restricted guides
    return false;
}

// Load all KNP guides for the current team
export async function loadKnpGuides() {
    if (!authState.team?.id) {
        console.warn("No team ID available, cannot load KNP guides");
        knpState.guides = [];
        return;
    }

    knpState.loading = true;
    knpState.error = null;

    try {
        const teamId = authState.team.id;
        const knpRef = db.collection("teams").doc(teamId).collection("knp");
        
        const querySnapshot = await knpRef.orderBy("title").get();
        
        knpState.guides = [];
        querySnapshot.forEach((doc) => {
            const guide = { ...doc.data(), id: doc.id };
            // Only add guides the user has access to
            if (canUserAccessGuide(guide)) {
                knpState.guides.push(guide);
            }
        });
    } catch (error) {
        console.error("Error loading KNP guides:", error);
        knpState.error = error.message;
        knpState.guides = [];
    } finally {
        knpState.loading = false;
    }
}

// Load terms for a specific KNP guide
export async function loadKnpTerms(guideId) {
    if (!authState.team?.id || !guideId) {
        console.warn("Cannot load terms without team ID and guide ID");
        knpState.terms = [];
        return;
    }

    knpState.loading = true;
    knpState.error = null;

    try {
        const teamId = authState.team.id;
        const termsRef = db.collection("teams").doc(teamId).collection("knp").doc(guideId).collection("terms");
        
        const querySnapshot = await termsRef.orderBy("term").get();
        
        knpState.terms = [];
        querySnapshot.forEach((doc) => {
            knpState.terms.push({ ...doc.data(), id: doc.id });
        });
    } catch (error) {
        console.error("Error loading KNP terms:", error);
        knpState.error = error.message;
        knpState.terms = [];
    } finally {
        knpState.loading = false;
    }
}

// Select a KNP guide and load its terms
export async function selectKnpGuide(guide) {
    // Directly assign to trigger reactivity
    knpState.selectedGuide = guide;
    
    if (guide) {
        await loadKnpTerms(guide.id);
    } else {
        knpState.terms = [];
    }
}

// Add or update a KNP guide
export async function saveKnpGuide(guideData) {
    if (!authState.team?.id) {
        throw new Error("No team ID available");
    }

    if (!guideData.id || !guideData.title) {
        throw new Error("Guide ID and title are required");
    }

    const teamId = authState.team.id;
    const userEmail = firebase.auth().currentUser?.email || "unknown";
    const timestamp = firebase.firestore.Timestamp.fromDate(new Date());

    const guideInfo = {
        id: guideData.id,
        title: guideData.title,
        description: guideData.description || "",
        original_language: guideData.original_language || "en-US",
        languages: guideData.languages || ["en-US"],
        groups: guideData.groups || [],
        createdOn: guideData.createdOn || timestamp,
        updatedOn: timestamp,
        createdBy: guideData.createdBy || userEmail
    };

    try {
        const knpRef = db.collection("teams").doc(teamId).collection("knp");
        await knpRef.doc(guideInfo.id).set(guideInfo);

        // Update local state
        const existingIndex = knpState.guides.findIndex((g) => g.id === guideInfo.id);
        if (existingIndex > -1) {
            knpState.guides[existingIndex] = guideInfo;
        } else {
            knpState.guides = [...knpState.guides, guideInfo].sort((a, b) => a.title.localeCompare(b.title));
        }

        return guideInfo;
    } catch (error) {
        console.error("Error saving KNP guide:", error);
        throw error;
    }
}

// Delete a KNP guide
export async function deleteKnpGuide(guideId) {
    if (!authState.team?.id || !guideId) {
        throw new Error("Team ID and guide ID are required");
    }

    const teamId = authState.team.id;

    try {
        const knpRef = db.collection("teams").doc(teamId).collection("knp");
            
        // Delete all terms first
        const termsRef = knpRef.doc(guideId).collection("terms");
        const termsSnapshot = await termsRef.get();
        const batch = db.batch();
        
        termsSnapshot.forEach((doc) => {
            batch.delete(doc.ref);
        });
        
        await batch.commit();
        
        // Then delete the guide
        await knpRef.doc(guideId).delete();

        // Update local state
        knpState.guides = knpState.guides.filter((g) => g.id !== guideId);
        
        if (knpState.selectedGuide?.id === guideId) {
            knpState.selectedGuide = null;
            knpState.terms = [];
        }
    } catch (error) {
        console.error("Error deleting KNP guide:", error);
        throw error;
    }
}

// Add or update a term in the current guide
export async function saveKnpTerm(termData) {
    if (!authState.team?.id || !knpState.selectedGuide?.id) {
        throw new Error("Team ID and selected guide are required");
    }

    if (!termData.id || !termData.term) {
        throw new Error("Term ID and term text are required");
    }

    const teamId = authState.team.id;
    const guideId = knpState.selectedGuide.id;

    const termInfo = {
        id: termData.id,
        term: termData.term,
        type: termData.type || "phrase",
        translations: termData.translations || {}
    };

    try {
        const termsRef = db.collection("teams").doc(teamId).collection("knp").doc(guideId).collection("terms");
        console.log(guideId, termInfo);
        await termsRef.doc(termInfo.id).set(termInfo);

        // Update local state
        const existingIndex = knpState.terms.findIndex((t) => t.id === termInfo.id);
        if (existingIndex > -1) {
            knpState.terms[existingIndex] = termInfo;
        } else {
            knpState.terms.push(termInfo);
        }

        return termInfo;
    } catch (error) {
        console.error("Error saving KNP term:", error);
        throw error;
    }
}

// Delete a term from the current guide
export async function deleteKnpTerm(termId) {
    if (!authState.team?.id || !knpState.selectedGuide?.id || !termId) {
        throw new Error("Team ID, selected guide, and term ID are required");
    }

    const teamId = authState.team.id;
    const guideId = knpState.selectedGuide.id;

    try {
        const termsRef = db.collection("teams").doc(teamId).collection("knp").doc(guideId).collection("terms");
        await termsRef.doc(termId).delete();

        // Update local state
        knpState.terms = knpState.terms.filter((t) => t.id !== termId);
    } catch (error) {
        console.error("Error deleting KNP term:", error);
        throw error;
    }
}

// Batch save terms (useful for CSV import)
export async function batchSaveTerms(termsArray) {
    if (!authState.team?.id || !knpState.selectedGuide?.id) {
        throw new Error("Team ID and selected guide are required");
    }

    const teamId = authState.team.id;
    const guideId = knpState.selectedGuide.id;

    try {
        const termsRef = db.collection("teams").doc(teamId).collection("knp").doc(guideId).collection("terms");
        const batch = db.batch();
        
        termsArray.forEach((term) => {
            if (term.id && term.term) {
                const termRef = termsRef.doc(term.id);
                batch.set(termRef, {
                    id: term.id,
                    term: term.term,
                    type: term.type || "phrase",
                    translations: term.translations || {}
                });
            }
        });
        
        await batch.commit();

        // Reload terms after batch save
        await loadKnpTerms(guideId);
    } catch (error) {
        console.error("Error batch saving KNP terms:", error);
        throw error;
    }
}

// Initialize - load guides if user is authenticated and has a team
if (authState.team?.id && typeof window !== 'undefined') {
    loadKnpGuides();
}
